import React from 'react';
import dayjs from 'dayjs';
import type { PurchaseRequisition } from '@/services/purchaseRequisition.service';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { withSpecificationLine } from './LineSpecifications';
import { headerProjectLabel, projectLabel } from '@/utils/projectDisplay';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { documentLineDimensions } from '@/utils/inventory/measurementQuantity';
import { formatKg } from '@/services/engineeringMaterial.service';

export interface PrintablePurchaseRequisitionProps {
  pr: PurchaseRequisition;
  onClose?: () => void;
}

/** Purchase requisition — shared pagination engine with restored columns. */
export const PrintablePurchaseRequisition: React.FC<PrintablePurchaseRequisitionProps> = ({
  pr,
  onClose,
}) => {
  const lines = pr.items ?? [];
  const projectHeader = headerProjectLabel(pr, lines);

  return (
    <EngineCommercialA4Print
      docType="purchase_requisition"
      title="PURCHASE REQUISITION"
      documentNumber={pr.pr_number || 'PR'}
      partyName={projectHeader !== '-' ? projectHeader : pr.department || ''}
      onClose={onClose}
      metaLines={[
        { label: 'PR No', value: pr.pr_number || '' },
        { label: 'Date', value: pr.pr_date ? dayjs(pr.pr_date).format('DD MMM YYYY') : '' },
        {
          label: 'Required',
          value: pr.required_date ? dayjs(pr.required_date).format('DD MMM YYYY') : '',
        },
        { label: 'Status', value: pr.pr_status || '' },
        { label: 'Project', value: projectHeader !== '-' ? projectHeader : '' },
        { label: 'Department', value: pr.department || '' },
        { label: 'Notes', value: pr.notes || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'code', label: 'Item' },
        { key: 'name', label: 'Item Name' },
        { key: 'description', label: 'Description' },
        { key: 'project', label: 'Project' },
        { key: 'dims', label: 'Dimensions' },
        { key: 'pcs', label: 'Pcs', align: 'right' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'uom', label: 'UOM' },
      ]}
      lines={lines.map((line) => ({
        lineNumber: line.line_number,
        description: [
          withSpecificationLine(
            formatItemDescriptionWithProfile(line.item_description, {
              profile_grade: line.profile_grade,
              profile_heat_no: line.profile_heat_no,
              profile_dimension: line.profile_dimension,
              profile_length: line.profile_length,
            }) || '—',
            line.specifications
          ),
          line.theoretical_weight != null ? `Theoretical weight: ${formatKg(line.theoretical_weight)}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
        cells: {
          code: line.item_code || '—',
          name:
            (line.line_kind === 'description_only'
              ? line.item_description
              : line.item_name
            )?.trim() || '—',
          project: projectLabel(line),
          dims: documentLineDimensions(line) || '—',
          pcs: line.piece_quantity != null ? Number(line.piece_quantity).toLocaleString() : '—',
          qty: Number(line.requested_quantity).toLocaleString(),
          uom: line.uom || '—',
        },
      }))}
    />
  );
};
